import {Component, computed, effect, model, OnDestroy, signal, Signal, TrackByFunction, untracked} from '@angular/core';
import {parseTypedTime, toLocalISOString} from '../../../../util/date.utils';
import {IconBtnComponent} from '../../icon-b/icon-btn.component';
import {Card} from "primeng/card";
import {CreateTaBaseComponent} from '../create-ta-base-component.directive';
import {Tab, TabList, Tabs} from 'primeng/tabs';
import {FormsModule} from '@angular/forms';
import {Textarea} from 'primeng/textarea';
import {TableModule} from 'primeng/table';
import {DatePicker} from 'primeng/datepicker';
import {AutoComplete, AutoCompleteCompleteEvent} from 'primeng/autocomplete';
import {Select} from 'primeng/select';
import {CurrencyPipe, DatePipe, NgClass} from '@angular/common';
import {TaToastComponent} from '../../ta-toast/ta-toast.component';
import {
  BaseOption,
  ItineraryItemDetails,
  LocationCategory,
  LocationOption, TravellerDetails,
  UserTravelItinerary
} from '../../../../api';
import {Checkbox} from 'primeng/checkbox';
import {InputText} from 'primeng/inputtext';
import {RadioButton} from 'primeng/radiobutton';
import {FloatLabel} from 'primeng/floatlabel';
import {ItineraryItemDetailsExtended, MealTypes, UserTravelItineraryExtended} from '../../../../util/app.types';
import {LocationService} from '../../../../services/location.service';
import {Tooltip} from 'primeng/tooltip';
import {ToggleSwitch} from 'primeng/toggleswitch';
import {PermissionService} from '../../../../services/permission.service';
import {fuzzySearch} from '../../../../util/search.utils';
import {itineraryDayRowClass} from '../../../../util/general.utils';
import {enabledMealTypes, hasOverseasValue, latestItinerariesForSubmission} from '../../../../util/itinerary.utils';

type FlightPlaceholderRow = {placeholder: true};

@Component({
  selector: 'create-ta-itinerary',
  imports: [
    IconBtnComponent,
    Card,
    Tab,
    TabList,
    Tabs,
    FormsModule,
    Textarea,
    TableModule,
    DatePicker,
    AutoComplete,
    Select,
    DatePipe,
    NgClass,
    TaToastComponent,
    Checkbox,
    InputText,
    RadioButton,
    FloatLabel,
    Tooltip,
    ToggleSwitch,
    CurrencyPipe,
  ],
  templateUrl: './create-ta-itinerary.component.html',
  styleUrl: './create-ta-itinerary.component.scss'
})
export class CreateTaItineraryComponent extends CreateTaBaseComponent implements OnDestroy {
  readonly currentItinerary: Signal<UserTravelItinerary | undefined> = this.store.currentItinerary;
  currentItineraryExtended = computed<UserTravelItineraryExtended>(() => {
    let current = this.currentItinerary();
    let extendedItinerary: UserTravelItineraryExtended = {
      ...current,
      items: current?.items ? current!.items!.map((item, index) => {
        const startLocation = this.taSubmissionDatasource.findLocation(item.startLocationId ?? '', item.startLocationNameId ?? '');
        const endLocation = this.taSubmissionDatasource.findLocation(item.endLocationId ?? '', item.endLocationNameId ?? '');
        // Overseas when either end is overseas, matching the server's estimator
        const isOverseas = startLocation?.category === LocationCategory.Overseas
          || endLocation?.category === LocationCategory.Overseas;
        return {
          ...item,
          startDate: item.start ? new Date(item.start) : undefined,
          endDate: item.end ? new Date(item.end) : undefined,
          startLocation,
          endLocation,
          supportingInfoLabel: this.getSupportingInfoLabel(item.travelMode ?? ''),
          // allowedFields: this.calculateEnabledFields(item, index, current),
          allowedFields: this.calculateServerEnabledFields(item, !!current?.isManual, isOverseas),
          isOverseas,
        } as ItineraryItemDetailsExtended;
      }) : [],
    };

    let disabledDates: Date[] = [];
    extendedItinerary.items!.forEach((item: ItineraryItemDetailsExtended) => {
      if (item.startDate) {
        disabledDates.push(item.startDate);
      }
    });
    extendedItinerary.disabledDates = [...disabledDates];
    return extendedItinerary;
  });

  /**
   * Rows are tracked by their server id, not by object identity. PrimeNG's Table defaults
   * rowTrackBy to `(index, item) => item`, and currentItineraryExtended above re-maps every item
   * into a new object on each recompute - so every save response tore down and rebuilt every row,
   * taking the p-autocomplete/p-select/p-datepicker the user had open with it. appendTo="body" does
   * not save it: the panel is on the body but belongs to the destroyed row component.
   *
   * Every rendered row has an id - addRow() enqueues and waits for the server rather than
   * inserting a local placeholder - so the index fallback only covers a row that somehow arrives
   * without one. Held as a field rather than a method so the input keeps one identity across
   * renders.
   */
  trackItineraryItem: TrackByFunction<ItineraryItemDetailsExtended | FlightPlaceholderRow> =
    (index, item) => 'placeholder' in item ? index : item.id ?? index;

  // A one-way flight-only itinerary has one item; pad a disabled Second Flight row as per the design.
  flightRows = computed<(ItineraryItemDetailsExtended | FlightPlaceholderRow)[]>(() => {
    const items = this.currentItineraryExtended().items;
    return this.isFlightOnly() && items.length === 1 ? [...items, {placeholder: true}] : items;
  });

  // Overseas cost column shows when any traveller on this TA has an overseas claim
  showOverseasColumn = computed<boolean>(() =>
    hasOverseasValue(this.currentItinerary()?.items ?? [])
    || latestItinerariesForSubmission(this.taSubmissionDatasource.allItineraries(), this.taSubmission()?.id)
      .some(itinerary => hasOverseasValue(itinerary.items ?? [])));

  outOfRegionManuallyClosed = signal<boolean>(false);
  showOutOfRegion = computed<boolean>(() => !this.isFlightOnly() &&
    this.currentItineraryExtended().items.some((item) =>
        (item.startLocation && !item.startLocation.inRegion) ||
        (item.endLocation && !item.endLocation.inRegion)
    ) && !this.outOfRegionManuallyClosed()
  );
  // Parent may set the initial traveller and is notified of changes.
  selectedTravellerId = model<string>('');

  enabledLocations = computed<LocationOption[]>(() =>
    this.store.availableLocations().filter((l: LocationOption) => !!l.enabled));
  filteredLocations = signal<LocationOption[]>([]);
  // Enabled locations this itinerary already uses, in itinerary order; offered first on a blank search.
  recentLocations = computed<LocationOption[]>(() => {
    const seen = new Set<string>();
    return this.currentItineraryExtended().items
      .flatMap(item => [item.startLocation, item.endLocation])
      .filter((location): location is LocationOption => {
        const key = `${location?.locationId}|${location?.nameId}`;
        if (!location?.enabled || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
  });
  // How many leading filteredLocations are recents, for the divider under the last one.
  recentLocationCount = signal<number>(0);
  availableWorkTypes: BaseOption[] = [];
  availableModesOfTravel: BaseOption[] = [];
  availableAccommodationTypes: BaseOption[] = [];

  // Picker whose time was picked/typed but not yet saved; keyed so a late onClose can't commit another picker.
  private stagedTime: { itemId: ItineraryItemDetails['id']; field: 'start' | 'end' } | null = null;
  canActivateManualOverride = signal<boolean>(false);

  // using a separate signal for the notes. Binding the text area to currentItinerary().notes
  // has pretty poor performance. Text input was very jittery and slow.
  workingNotes = signal<string>('');
  private notesDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSyncedItineraryId: string | null = null;

  constructor(
    private locationService: LocationService,
    private permissionService: PermissionService,
  ) {
    super();
    // Sync workingNotes from the store only when the active itinerary changes
    // (e.g. switching traveller tabs). This avoids clobbering in-flight typing
    // when other actions update currentItinerary.
    effect(() => {
      const itinerary = this.currentItinerary();
      const id = itinerary?.id ?? null;
      if (id !== this.lastSyncedItineraryId) {
        this.lastSyncedItineraryId = id;
        untracked(() => this.workingNotes.set(itinerary?.notes ?? ''));
      }
    });

    // Undo a pre-payment request once the itinerary is a single day trip. Only an editor of an
    // in-progress TA may save travellers; the read-only and post-approval uses of this step may not.
    effect(() => {
      this.currentItinerary();
      this.taSubmission();
      untracked(() => {
        if (this.permissionService.canEditTaPrimary()) {
          this.taSubmissionDatasource.clearDayTripPrepayment();
        }
      });
    });

    // Seed the default suggestions once the location list lands. loadLocations() is a fire-and-forget
    // fetch, so reading availableLocations() straight after it left this empty on a cold load and the
    // dropdown stayed blank until the user typed. Cap the list: the full ~1300 are slow to render when
    // the dropdown opens, and as of primeng v21.1.5 autocomplete virtual scrolling resets the selection
    // (https://github.com/primefaces/primeng/issues/13655, .../16063).
    effect(() => {
      const locations = this.enabledLocations();
      untracked(() => {
        this.recentLocationCount.set(0);
        this.filteredLocations.set(locations.slice(0, 250));
      });
    });
  }

  override ngOnInit() {
    super.ngOnInit();

    this.availableWorkTypes = this.store.session()?.details?.workTypeOptions ?? [];
    this.availableModesOfTravel = this.store.session()?.details?.travelModeOptions ?? [];
    this.availableAccommodationTypes = this.store.session()?.details?.accommodationTypeOptions ?? [];

    this.locationService.loadLocations();

    const primaryId = this.taSubmission()?.primaryTraveller?.travellerId
    if (primaryId) {
      this.taSubmissionDatasource.loadTravellerItinerary(primaryId);
    }

    const travellers = this.taSubmission()?.travellers ?? [];
    // Parent-requested traveller (e.g. selected in read-only view), if on this TA and editable here.
    const requested = travellers.find(t => t.travellerId === this.selectedTravellerId());
    const userTraveller = travellers.find(t => t.details?.id === this.store.session()?.details?.user?.id);
    const id = (requested && (this.isReadOnly() || this.allowedTab(requested)) ? requested.travellerId : undefined)
      ?? userTraveller?.travellerId ?? this.taSubmission()?.primaryTraveller?.travellerId;
    if (id) {
      this.taSubmissionDatasource.loadTravellerItinerary(id);
      this.selectedTravellerId.set(id);
    }
    this.canActivateManualOverride.set(this.permissionService.canOverrideItinerary())
  }

  onTravellerTabChange(event: any) {
    this.taSubmissionDatasource.loadTravellerItinerary(event);
    this.selectedTravellerId.set(event);
  }

  updateIsManual(eventValue: any): void {
    this.taSubmissionDatasource.setManualMode(this.currentItinerary()?.id ?? '', eventValue);
  }

  updateItineraryItemField(itineraryItem: ItineraryItemDetails, field: keyof ItineraryItemDetails, event: any): void {
    // console.log(field, event);
    // Deep copy: the item's nested objects (e.g. mealClaims) are shared by reference
    // with the datasource's itinerary cache, so in-place edits must not leak into it.
    let newItineraryItem: ItineraryItemDetails = structuredClone(itineraryItem);

    if (field === 'startLocationId') {
      newItineraryItem.startLocationId = event.value.locationId;
      newItineraryItem.startLocationNameId = event.value.nameId;
      if (!(event.value as LocationOption).inRegion) {
        this.outOfRegionManuallyClosed.set(false);
      }
    } else if (field === 'endLocationId') {
      newItineraryItem.endLocationId = event.value.locationId;
      newItineraryItem.endLocationNameId = event.value.nameId;
      if (!(event.value as LocationOption).inRegion) {
        this.outOfRegionManuallyClosed.set(false);
      }
    } else if (field === 'cateringOption') {
      newItineraryItem.cateringOption = event.value;
    } else if (field === 'mealClaims') {
      if (!newItineraryItem.mealClaims) {
        newItineraryItem.mealClaims = {};
      }
      Object.assign(newItineraryItem.mealClaims, event);
      this.taSubmissionDatasource.saveCurrentItineraryItem({...newItineraryItem, travellerId: this.selectedTravellerId()}, 'mealClaims');
      return;
    } else if (field === 'supportingInfo') {
      newItineraryItem.supportingInfo = (event.target as HTMLInputElement).value;
    } else if (field === 'start') {
      newItineraryItem.start = event; //event must be an ISO string
    } else if (field === 'end') {
      newItineraryItem.end = event; //event must be an ISO string
    } else if (field === 'workType') {
      newItineraryItem.workType = event.value;
    } else if (field === 'accommodationType') {
      newItineraryItem.accommodationType = event.value;
    } else if (field === 'travelMode') {
      newItineraryItem.travelMode = event.value;
    } else if (field === 'isPersonalDay') {
      newItineraryItem.isPersonalDay = event;
    } else {
      newItineraryItem[field] = event.value;
    }
    this.taSubmissionDatasource.saveCurrentItineraryItem({...newItineraryItem, travellerId: this.selectedTravellerId()});
  }

  // Fired by both the spinner and typing; marks the picker dirty for commitTime.
  stageTime(itineraryItem: ItineraryItemDetails, field: 'start' | 'end'): void {
    this.stagedTime = {itemId: itineraryItem.id, field};
  }

  // Called on overlay close and on blur; whichever fires first commits, the other finds nothing staged.
  // Parses the input's text (PrimeNG's own parse misreads e.g. "7:30pm" as 07:30; the spinner keeps the
  // text in sync), rounds to 15 min and saves on the item's own date. Unparseable or end <= start reverts.
  commitTime(itineraryItem: ItineraryItemDetails, field: 'start' | 'end', picker: DatePicker): void {
    const staged = this.stagedTime;
    if (!staged || staged.itemId !== itineraryItem.id || staged.field !== field) {
      return;
    }
    this.stagedTime = null;

    const current = new Date(itineraryItem[field]!);
    const typed = parseTypedTime(picker.inputfieldViewChild?.nativeElement.value ?? '');
    if (!typed) {
      picker.writeValue(current);
      return;
    }
    // Clamp to 23:45 so rounding never rolls into the next day.
    const minutes = Math.min(Math.round((typed.hours * 60 + typed.minutes) / 15) * 15, 23 * 60 + 45);
    const next = new Date(current);
    next.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    // End must be strictly after start; a commit that breaks this reverts like invalid text.
    const other = field === 'start' ? itineraryItem.end : itineraryItem.start;
    if (other && (field === 'start' ? next >= new Date(other) : next <= new Date(other))) {
      picker.writeValue(current);
      return;
    }
    picker.writeValue(next);
    if (next.getTime() !== current.getTime()) {
      this.updateItineraryItemField(itineraryItem, field, toLocalISOString(next));
    }
  }

  updateItineraryDate(itineraryEvent: ItineraryItemDetails, dateStr: string): void {
    //Fix up end day
    let newEndDate = new Date(dateStr);
    if (!itineraryEvent.end) {
      newEndDate.setHours(16);
      newEndDate.setMinutes(30);
    } else {
      let oldEndDate = new Date(itineraryEvent.end);
      newEndDate.setHours(oldEndDate.getHours());
      newEndDate.setMinutes(oldEndDate.getMinutes());
    }

    //AND set start
    let newStartDate = new Date(dateStr);
    if (!itineraryEvent.start) {
      newStartDate.setHours(8);
      newStartDate.setMinutes(30);
    } else {
      let oldStartDate = new Date(itineraryEvent.start);
      newStartDate.setHours(oldStartDate.getHours());
      newStartDate.setMinutes(oldStartDate.getMinutes());
    }
    this.taSubmissionDatasource.saveCurrentItineraryItem({...itineraryEvent, end: toLocalISOString(newEndDate), start: toLocalISOString(newStartDate),  travellerId: this.selectedTravellerId()});
  }


  onNotesChange(value: string): void {
    this.workingNotes.set(value);
    if (this.notesDebounceTimer) {
      clearTimeout(this.notesDebounceTimer);
    }
    this.notesDebounceTimer = setTimeout(
      () => this.flushNotes(),
      500,
    );
  }

  onNotesBlur(): void {
    this.flushNotes();
  }

  private flushNotes(): void {
    if (this.notesDebounceTimer) {
      clearTimeout(this.notesDebounceTimer);
      this.notesDebounceTimer = null;
    }
    const pending = this.workingNotes();
    if (pending !== (this.currentItinerary()?.notes ?? '')) {
      this.taSubmissionDatasource.updateItineraryNotes(pending);
    }
  }

  override ngOnDestroy(): void {
    this.flushNotes();
    super.ngOnDestroy();
  }

  filterLocations(event: AutoCompleteCompleteEvent) {
    const searching = !!event.query.trim();
    const recents = searching ? [] : this.recentLocations();
    this.recentLocationCount.set(recents.length);
    this.filteredLocations.set([
      ...recents,
      // Top 10 matches while searching; otherwise the first 150 locations.
      ...fuzzySearch(this.enabledLocations(), event.query, 'name').slice(0, searching ? 10 : 150),
    ]);
  }

  // Select the whole name on focus so typing replaces it. Deferred past the click's mouseup, which
  // would otherwise place the caret; skipped if the user has since moved on or typed.
  selectLocationText(event: Event) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    setTimeout(() => {
      if (document.activeElement === input && input.value === value) {
        input.select();
      }
    });
  }

  addRow(): void {
    this.taSubmissionDatasource.addCurrentItineraryItem(
    {
      itineraryId: this.currentItinerary()!.id!,
      travellerId: this.selectedTravellerId()!
    });
    // this.newItineraryItemStart.set(undefined);
  }

  removeRow(itineraryItem: ItineraryItemDetails): void {
    this.taSubmissionDatasource.removeCurrentItineraryItem({...itineraryItem, travellerId: this.selectedTravellerId()});
  }

  getSupportingInfoLabel(travelMode: string): string {
    if (travelMode.toLowerCase().includes('car')) {
      return 'Number plate...';
    } else if (travelMode === 'Air') {
      return 'Flight number...';
    }
    return 'Enter...';
  }

  //////  Departure Allowed meals
  //////   [0-6:59] -> ['Breakfast','Lunch','Dinner']
  //////   [7:00-12:59] -> ['Lunch','Dinner']
  //////   [13:00-18:59] -> ['Dinner']
  //////   [19:00-23:59] -> []

  //////  Return Allowed Meals
  //////   [0-6:59] -> []
  //////   [7:00-12:59] -> ['Breakfast']
  //////   [13:00-18:59] -> ['Breakfast', 'Lunch']
  //////   [19:00-23:59] -> ['Breakfast', 'Lunch', 'Dinner']

  // departureDayAllowedMeals(hours: number, minutes: number): string[] {
  //   const totalMinutes = hours*60 + minutes;
  //   if (totalMinutes < 7*60) {
  //     return [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner];
  //   } else if (totalMinutes < 13*60) {
  //     return [MealTypes.Lunch, MealTypes.Dinner];
  //   } else if (totalMinutes < 19*60) {
  //     return [MealTypes.Dinner];
  //   }
  //   return [];
  // }
  //
  // returnDayAllowedMeals(hours: number, minutes: number): string[] {
  //   const totalMinutes = hours*60 + minutes;
  //   if (totalMinutes < 7*60) {
  //     return [];
  //   } else if (totalMinutes < 13*60) {
  //     return  [MealTypes.Breakfast];
  //   } else if (totalMinutes < 19*60) {
  //     return [MealTypes.Breakfast, MealTypes.Lunch];
  //   }
  //   return [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner];
  // }
  //
  // // Calculation for allowed meals/camping , currently doesn't account for field work
  // // 24hr period running over the departure time on the transition day field -> Other
  // calculateEnabledFields(item: ItineraryItemDetails, itemIndex: number, itinerary: UserTravelItinerary): string[] {
  //   if (itinerary.isManual) {
  //     return [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner, MealTypes.Camping];
  //   }
  //
  //   const itineraryLength = itinerary.items?.length ?? -1;
  //
  //   if (!item.workType || item.workType === 'Day' || itineraryLength === -1 || item.isPersonalDay) {
  //     return [];
  //   }
  //
  //   if (itemIndex === 0) {
  //     if (item.workType === 'Other' && item.start) { // First Day
  //       let departureTime = new Date(item.start);
  //       return this.departureDayAllowedMeals(departureTime.getHours(), departureTime.getMinutes());
  //     } else if (item.workType === 'Field') {
  //       return [MealTypes.Camping];
  //     } else {
  //       return [];
  //     }
  //   } else if (itemIndex === itineraryLength - 1) { //Last Day
  //     if (item.workType === 'Other' && item.end) {
  //       let departureTime = new Date(item.end);
  //       return this.returnDayAllowedMeals(departureTime.getHours(), departureTime.getMinutes());
  //     } else if (item.workType === 'Field') {
  //       return [];
  //     } else {
  //       return [];
  //     }
  //   } else { // Intermediate Day
  //     const previousItem = itinerary.items![itemIndex-1];
  //     if (previousItem.workType === 'Other' && item.workType === 'Other') {
  //       return [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner];
  //     } else if (previousItem.workType === 'Other' && item.workType === 'Field') {
  //       if (item.start) {
  //         let mealAllowanceStop = new Date(item.start);
  //         //This looks funny, but returnDayAllowedMeals is calculating allowed meals for before a 'time' (arrival/departure is context dependent).
  //         return [...this.returnDayAllowedMeals(mealAllowanceStop.getHours(), mealAllowanceStop.getMinutes()), MealTypes.Camping];
  //       } else {
  //         return [MealTypes.Camping];
  //       }
  //     } else if (previousItem.workType === 'Field' && item.workType === 'Other') {
  //       if (item.end) {
  //         let mealAllowanceStart = new Date(item.end);
  //         if (previousItem.campingStart) {
  //           let campingEnd = new Date(previousItem.campingStart);
  //           campingEnd.setDate(campingEnd.getDate() + 1);
  //           if (campingEnd.getTime() > mealAllowanceStart.getTime()) {
  //             mealAllowanceStart = new Date(campingEnd);
  //           }
  //         }
  //         //Get and use latest time
  //         //return this.departureDayAllowedMeals(latest.getHours(), latest.getMinutes());
  //         //This looks funny, but departureDayAllowedMeals is calculating allowed meals for after a 'time' (arrival/departure is context dependent).
  //         return this.departureDayAllowedMeals(mealAllowanceStart.getHours(), mealAllowanceStart.getMinutes());
  //       }
  //     } else if (previousItem.workType === 'Field' && item.workType === 'Field') {
  //       return [MealTypes.Camping];
  //     }
  //   }
  //   return [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner, MealTypes.Camping];
  // }

  calculateServerEnabledFields(item: ItineraryItemDetails, isManual: boolean, isOverseas: boolean): string[] {
    if (isManual && this.canActivateManualOverride()) {
      // Overseas days swap catering for the overseas meals & incidentals claim
      return isOverseas
        ? [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner, MealTypes.Overseas, MealTypes.DayTrip]
        : [MealTypes.Breakfast, MealTypes.Lunch, MealTypes.Dinner, MealTypes.Camping, MealTypes.Incidentals, MealTypes.DayTrip];
    }
    return enabledMealTypes(item.enabledMeals);
  }


  allowedTab(traveller: TravellerDetails): boolean {
    return this.permissionService.canEditTravellerItinerary(traveller);
  }

  protected readonly toLocalISOString = toLocalISOString;
  protected readonly itineraryDayRowClass = itineraryDayRowClass;
  protected readonly MealTypes = MealTypes;
}
